import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface LateCancellationRequest {
  id: string;
  booking_id: string;
  client_id: string;
  client_reason: string;
  created_at: string;
  start_time: string;
  service_name: string;
  client_name: string;
}

interface LateCancellationNotificationSystemProps {
  onNavigateToUpcoming: () => void;
}

export const LateCancellationNotificationSystem: React.FC<LateCancellationNotificationSystemProps> = ({
  onNavigateToUpcoming
}) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('dashboard');
  const [pendingRequests, setPendingRequests] = useState<LateCancellationRequest[]>([]);
  const [showNotification, setShowNotification] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<LateCancellationRequest | null>(null);

  const locale = i18n.language === 'zh' ? zhCN : enUS;

  useEffect(() => {
    if (!user?.id) return;

    // Fetch existing pending late cancellation requests
    const fetchPendingRequests = async () => {
      console.log('🔍 Fetching pending late cancellation requests for practitioner:', user.id);

      const { data, error } = await supabase
        .from('late_cancellation_requests')
        .select(`
          id,
          booking_id,
          client_id,
          client_reason,
          created_at,
          bookings!inner(
            start_time,
            services!inner(service_name),
            clients!inner(first_name, last_name)
          )
        `)
        .eq('practitioner_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending late cancellation requests:', error);
        return;
      }

      if (data) {
        const formattedRequests = data.map(request => ({
          id: request.id,
          booking_id: request.booking_id,
          client_id: request.client_id,
          client_reason: request.client_reason || '',
          created_at: request.created_at,
          start_time: (request.bookings as any).start_time,
          service_name: (request.bookings as any).services?.service_name || '',
          client_name: `${(request.bookings as any).clients?.first_name || ''} ${(request.bookings as any).clients?.last_name || ''}`.trim()
        }));

        console.log('📊 Found', formattedRequests.length, 'pending late cancellation requests');
        setPendingRequests(formattedRequests);

        // Show notification for the first pending request
        if (formattedRequests.length > 0) {
          setCurrentRequest(formattedRequests[0]);
          setShowNotification(true);
        }
      }
    };

    fetchPendingRequests();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('late_cancellation_requests_realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'late_cancellation_requests',
          filter: `practitioner_id=eq.${user.id}`
        },
        async (payload) => {
          console.log('🔔 New late cancellation request received:', payload);

          // Fetch the full request details with booking info
          const { data: newRequest } = await supabase
            .from('late_cancellation_requests')
            .select(`
              id,
              booking_id,
              client_id,
              client_reason,
              created_at,
              bookings!inner(
                start_time,
                services!inner(service_name),
                clients!inner(first_name, last_name)
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (newRequest) {
            const formattedRequest = {
              id: newRequest.id,
              booking_id: newRequest.booking_id,
              client_id: newRequest.client_id,
              client_reason: newRequest.client_reason || '',
              created_at: newRequest.created_at,
              start_time: (newRequest.bookings as any).start_time,
              service_name: (newRequest.bookings as any).services?.service_name || '',
              client_name: `${(newRequest.bookings as any).clients?.first_name || ''} ${(newRequest.bookings as any).clients?.last_name || ''}`.trim()
            };

            setPendingRequests(prev => [formattedRequest, ...prev]);
            setCurrentRequest(formattedRequest);
            setShowNotification(true);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleViewRequest = () => {
    setShowNotification(false);
    onNavigateToUpcoming();
  };

  const handleDismiss = () => {
    setShowNotification(false);
    // Move to next request if available
    const currentIndex = pendingRequests.findIndex(r => r.id === currentRequest?.id);
    if (currentIndex < pendingRequests.length - 1) {
      setCurrentRequest(pendingRequests[currentIndex + 1]);
      setShowNotification(true);
    } else {
      setCurrentRequest(null);
    }
  };

  if (!currentRequest) return null;

  return (
    <AlertDialog open={showNotification} onOpenChange={setShowNotification}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            Late Cancellation Request
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3 text-left">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-red-900">
                {currentRequest.client_name} has requested to cancel within 24 hours:
              </p>
              <div className="text-sm space-y-1">
                <p><span className="font-medium">Service:</span> {currentRequest.service_name}</p>
                <p>
                  <span className="font-medium">Requested:</span>{' '}
                  {formatDistanceToNow(new Date(currentRequest.created_at), {
                    addSuffix: true,
                    locale
                  })}
                </p>
              </div>
            </div>

            <div className="border rounded-lg p-3 bg-gray-50">
              <p className="text-sm font-medium mb-1">Client's Reason:</p>
              <p className="text-sm text-gray-700">{currentRequest.client_reason}</p>
            </div>

            {pendingRequests.length > 1 && (
              <p className="text-xs text-gray-500">
                {pendingRequests.length - 1} more cancellation request{pendingRequests.length > 2 ? 's' : ''} pending
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={handleDismiss}>
            Dismiss
          </Button>
          <Button onClick={handleViewRequest} className="bg-red-600 hover:bg-red-700">
            View in Upcoming
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
